import api from './api';
import { PaginationParams, ApiResponse } from '../types';

// Interfaces específicas para gateways
export interface Gateway {
  id: number;
  nome: string;
  tipo: 'asaas' | 'iugu' | 'efi' | 'sicoob';
  ativo: boolean;
  ambiente: 'sandbox' | 'producao';
  escritorio_id?: number | null;
  email_notificacoes?: string | null;

  // Campos para Asaas e Iugu
  api_token?: string;

  // Campos para Efi
  client_id?: string;
  client_secret?: string;

  // Campos para EFI (Gerencianet)
  certificado_path?: string;
  chave_pix?: string;

  // Campos bancários
  num_agencia?: string;
  num_conta?: string;
  nome_cedente?: string;
  cpf_cnpj?: string;
  local_pgto?: string;
  instrucoes?: string;

  is_default: boolean;
  emitir_nfcom_auto: boolean;
  transmitir_nfcom_auto: boolean;

  // Configurações adicionais
  configuracoes_extras?: any;

  // Webhook
  webhook_url?: string;

  // Status de conectividade
  ultimo_teste_conectividade?: Date;
  status_conectividade: 'conectado' | 'erro' | 'nao_testado';
  erro_conectividade?: string;

  // Observações
  observacoes?: string;

  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface CreateGatewayRequest {
  nome: string;
  tipo: 'asaas' | 'iugu' | 'efi' | 'sicoob';
  ativo?: boolean;
  ambiente?: 'sandbox' | 'producao';
  escritorio_id?: number | null;
  email_notificacoes?: string | null;

  // Campos para Asaas
  api_token?: string;

  // Campos para Iugu
  client_id?: string;
  client_secret?: string;

  // Campos para EFI (Gerencianet)
  certificado_path?: string;
  chave_pix?: string;

  // Campos bancários
  num_agencia?: string;
  num_conta?: string;
  nome_cedente?: string;
  cpf_cnpj?: string;
  local_pgto?: string;
  instrucoes?: string;

  is_default?: boolean;
  emitir_nfcom_auto?: boolean;
  transmitir_nfcom_auto?: boolean;

  // Configurações adicionais
  configuracoes_extras?: any;

  // Webhook
  webhook_url?: string;

  // Observações
  observacoes?: string;
}

export interface UpdateGatewayRequest {
  nome?: string;
  tipo?: 'asaas' | 'iugu' | 'efi' | 'sicoob';
  ativo?: boolean;
  ambiente?: 'sandbox' | 'producao';
  escritorio_id?: number | null;
  email_notificacoes?: string | null;

  // Campos para Asaas
  api_token?: string;

  // Campos para Iugu
  client_id?: string;
  client_secret?: string;

  // Campos para EFI (Gerencianet)
  certificado_path?: string;
  chave_pix?: string;

  // Campos bancários
  num_agencia?: string;
  num_conta?: string;
  nome_cedente?: string;
  cpf_cnpj?: string;
  local_pgto?: string;
  instrucoes?: string;

  is_default?: boolean;
  emitir_nfcom_auto?: boolean;
  transmitir_nfcom_auto?: boolean;

  // Configurações adicionais
  configuracoes_extras?: any;

  // Webhook
  webhook_url?: string;

  // Observações
  observacoes?: string;
}

export interface GatewaysResponse {
  gateways: Gateway[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface GatewayConnectionTest {
  conectado: boolean;
  status_conectividade: 'conectado' | 'erro' | 'nao_testado';
  erro_conectividade?: string;
  ultimo_teste_conectividade?: Date;
}

export interface GatewayStats {
  total: number;
  ativos: number;
  inativos: number;
  porTipo: { tipo: string; count: number }[];
  porAmbiente: { ambiente: string; count: number }[];
  porStatusConectividade: { status_conectividade: string; count: number }[];
}

export interface GatewayPaymentMethods {
  gateway_id: number;
  gateway_nome: string;
  gateway_tipo: string;
  metodos_pagamento: string[];
}

class GatewayService {
  /**
   * Lista todos os gateways com paginação
   */
  async getAllGateways(params?: PaginationParams & {
    tipo?: string;
    ativo?: string;
    ambiente?: string;
  }): Promise<GatewaysResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.tipo) queryParams.append('tipo', params.tipo);
      if (params?.ativo) queryParams.append('ativo', params.ativo);
      if (params?.ambiente) queryParams.append('ambiente', params.ambiente);

      const url = `/gateways?${queryParams.toString()}`;

      const response = await api.get<ApiResponse<Gateway[]> | Gateway[]>(url);

      // Verificar se a resposta é um array direto ou objeto estruturado
      if (Array.isArray(response.data)) {
        const result = {
          gateways: response.data,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: response.data.length,
            pages: 1,
          },
        };

        return result;
      }

      // Resposta é objeto estruturado
      const apiResponse = response.data as ApiResponse<Gateway[]>;
      if (apiResponse.success && apiResponse.data) {
        const result = {
          gateways: apiResponse.data,
          pagination: apiResponse.pagination || {
            page: 1,
            limit: 10,
            total: apiResponse.data.length,
            pages: 1,
          },
        };

        return result;
      }

      throw new Error(apiResponse.message || 'Erro ao buscar gateways');
    } catch (error: any) {
      console.error('❌ Erro no gatewayService.getAllGateways:', error);

      if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        throw new Error('Erro de conexão com o servidor');
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca gateways ativos para seleção
   */
  async getActiveGateways(): Promise<Gateway[]> {
    try {
      const response = await api.get<Gateway[]>('/gateways/active');

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se é um array, é a resposta direta
        if (Array.isArray(response)) {
          return response;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateways ativos:', error);
      throw error;
    }
  }

  /**
   * Busca um gateway específico por ID
   */
  async getGatewayById(id: number): Promise<Gateway> {
    try {
      const response = await api.get<Gateway>(`/gateways/${id}`);

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se tem as propriedades do Gateway, é o Gateway direto
        if ('id' in response && 'nome' in response && 'tipo' in response) {
          return response as unknown as Gateway;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateway:', error);
      throw error;
    }
  }

  /**
   * Busca gateways por tipo
   */
  async getGatewaysByType(tipo: 'asaas' | 'iugu' | 'efi' | 'sicoob'): Promise<Gateway[]> {
    try {
      const response = await api.get<Gateway[]>(`/gateways/tipo/${tipo}`);

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se é um array, é a resposta direta
        if (Array.isArray(response)) {
          return response;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateways por tipo:', error);
      throw error;
    }
  }

  /**
   * Cria um novo gateway
   */
  async createGateway(gatewayData: CreateGatewayRequest): Promise<Gateway> {
    try {
      // Validar dados obrigatórios
      if (!gatewayData.nome || !gatewayData.tipo) {
        throw new Error('Nome e tipo são obrigatórios');
      }

      const response = await api.post<Gateway>('/gateways', gatewayData);

      // O interceptador da API já retorna apenas response.data
      // Então response aqui é diretamente o Gateway ou a estrutura {success, data}
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se tem as propriedades do Gateway, é o Gateway direto
        if ('id' in response && 'nome' in response && 'tipo' in response) {
          return response as unknown as Gateway;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao criar gateway:', error);

      // O interceptador já trata os erros e mostra os toasts
      // Só re-lançamos o erro para ser capturado pelo componente
      throw error;
    }
  }

  /**
   * Atualiza um gateway existente
   */
  async updateGateway(id: number, gatewayData: UpdateGatewayRequest): Promise<Gateway> {
    try {
      const response = await api.put<Gateway>(`/gateways/${id}`, gatewayData);

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se tem as propriedades do Gateway, é o Gateway direto
        if ('id' in response && 'nome' in response && 'tipo' in response) {
          return response as unknown as Gateway;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao atualizar gateway:', error);
      throw error;
    }
  }

  /**
   * Exclui um gateway
   */
  async deleteGateway(id: number): Promise<void> {
    try {
      const response = await api.delete<any>(`/gateways/${id}`);

      // O interceptador da API já retorna apenas response.data
      // Para DELETE, só precisamos verificar se não houve erro
      if (response && typeof response === 'object') {
        // Se tem success e é true, ou se não tem success (indicando sucesso)
        if (!('success' in response) || (response as any).success) {
          return;
        }
      }

      throw new Error('Erro ao excluir gateway');
    } catch (error: any) {
      console.error('Erro ao excluir gateway:', error);
      throw error;
    }
  }

  /**
   * Ativar/Desativar um gateway
   */
  async toggleGatewayStatus(id: number): Promise<Gateway> {
    try {
      const response = await api.patch<Gateway>(`/gateways/${id}/toggle-status`);

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se tem as propriedades do Gateway, é o Gateway direto
        if ('id' in response && 'nome' in response && 'tipo' in response) {
          return response as unknown as Gateway;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao alterar status do gateway:', error);
      throw error;
    }
  }

  /**
   * Testar conectividade do gateway
   */
  async testGatewayConnection(id: number): Promise<GatewayConnectionTest> {
    try {
      const response = await api.post<GatewayConnectionTest>(`/gateways/${id}/test-connection`);

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se tem as propriedades do GatewayConnectionTest, é o objeto direto
        if ('conectado' in response && 'status_conectividade' in response) {
          return response as unknown as GatewayConnectionTest;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao testar conectividade:', error);
      throw error;
    }
  }

  /**
   * Obter estatísticas dos gateways
   */
  async getGatewayStats(): Promise<GatewayStats> {
    try {
      const response = await api.get<GatewayStats>('/gateways/stats');

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se tem as propriedades do GatewayStats, é o objeto direto
        if ('total' in response && 'ativos' in response) {
          return response as unknown as GatewayStats;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  }

  /**
   * Obter métodos de pagamento suportados por um gateway
   */
  async getGatewayPaymentMethods(id: number): Promise<GatewayPaymentMethods> {
    try {
      const response = await api.get<GatewayPaymentMethods>(`/gateways/${id}/payment-methods`);

      // O interceptador da API já retorna apenas response.data
      if (response && typeof response === 'object') {
        // Se tem success e data, significa que não passou pelo interceptador corretamente
        if ('success' in response && 'data' in response && (response as any).success) {
          return (response as any).data;
        }

        // Se tem as propriedades do GatewayPaymentMethods, é o objeto direto
        if ('gateway_id' in response && 'metodos_pagamento' in response) {
          return response as unknown as GatewayPaymentMethods;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar métodos de pagamento:', error);
      throw error;
    }
  }

  // Métodos utilitários
  getGatewayDisplayName(tipo: 'asaas' | 'iugu' | 'efi' | 'sicoob'): string {
    const names: Record<string, string> = {
      asaas: 'Asaas',
      iugu: 'Iugu',
      efi: 'EFI',
      sicoob: 'Sicoob'
    };
    return names[tipo] || tipo;
  }

  getGatewayColor(tipo: 'asaas' | 'iugu' | 'efi' | 'sicoob'): string {
    const colors: Record<string, string> = {
      asaas: '#1e88e5',
      iugu: '#ff9800',
      efi: '#4caf50',
      sicoob: '#003641'
    };
    return colors[tipo] || '#9e9e9e';
  }

  getGatewayIcon(tipo: 'asaas' | 'iugu' | 'efi' | 'sicoob'): string {
    const icons: Record<string, string> = {
      asaas: '💳',
      iugu: '🟠',
      efi: '🔄',
      sicoob: '🏦'
    };
    return icons[tipo] || '💰';
  }

  getGatewayLogoIcon(tipo: 'asaas' | 'iugu' | 'efi' | 'sicoob'): string {
    const icons: Record<string, string> = {
      asaas: 'asaas.png',
      iugu: 'iugu.png',
      efi: 'efibank.png',
      sicoob: 'sicoob.png'
    };
    return icons[tipo] || '💰';
  }

  getStatusConnectivityColor(status: 'conectado' | 'erro' | 'nao_testado'): string {
    const colors = {
      conectado: '#4caf50',
      erro: '#f44336',
      nao_testado: '#ff9800'
    };
    return colors[status] || '#9e9e9e';
  }

  getStatusConnectivityLabel(status: 'conectado' | 'erro' | 'nao_testado'): string {
    const labels = {
      conectado: 'Conectado',
      erro: 'Erro',
      nao_testado: 'Não testado'
    };
    return labels[status] || status;
  }

  /**
   * Atualiza a URL de notificação nas faturas do gateway Efíbank
   */
  async atualizarNotificationUrl(
    gatewayId: number
  ): Promise<{
    total: number;
    sucessos: number;
    erros: number;
    detalhesErros?: string[];
  }> {
    const response: any = await api.post(`/gateways/${gatewayId}/atualizar-notification-url`);
    if (response && response.data) {
      return response.data;
    }
    return response;
  }
}

const gatewayService = new GatewayService();
export { gatewayService };
export default gatewayService; 